import PropTypes from 'prop-types';
import { useState } from 'react';
import { Field } from 'redux-form';
import { RadioBlock } from '../../forms/RadioBlock/RadioBlock.jsx';
import { Modal } from '../Modal/Modal';
import './consentement-email.scss';

/* Liens propres à chaque mandant : le courtier (AMGestion) n'a pas les mêmes pages légales que le direct */
const MANDANTS = {
    aprilMoto: {
        nom: 'APRIL Moto',
        politiqueCookies: 'https://cms-api.april-moto.com/storage/documents/politique-cookies-et-technologies-similaires-0926.pdf',
        lettreRgpd: 'https://devis.april-moto.com/mentions-legales/donnees-personnelles.html',
    },
    amGestion: {
        nom: 'AMGestion',
        politiqueCookies: 'https://cms-api.april-moto.com/storage/documents/politique-cookies-et-technologies-similaires-0926.pdf',
        lettreRgpd: 'https://www.amgestionassurance.com/Doc/Lettre-RGPD-AM-Gestion-11.2021.pdf',
    },
};

const required = (value) => (value === 'true' || value === 'false' ? undefined : 'Merci de renseigner vos préférences.');

const LienExterne = ({ href, className, children }) => (
    <a href={href} target={'_blank'} rel={'noopener noreferrer'} className={className}>
        {children}
        <span className={'visually-hidden'}> (nouvelle fenêtre)</span>
    </a>
);

const ChoixConsentement = ({ name, titre, children }) => (
    <fieldset className={'consentement-email__choix'}>
        <legend className={'consentement-email__titre-choix'}>{titre}</legend>
        <div className={'consentement-email__texte'}>{children}</div>
        <div className={'row g-2 mt-1'}>
            <div className={'col-6'}>
                <Field component={RadioBlock} type={'radio'} name={name} value={'true'} label={"J'accepte"} validate={required} />
            </div>
            <div className={'col-6'}>
                <Field component={RadioBlock} type={'radio'} name={name} value={'false'} label={'Je refuse'} validate={required} />
            </div>
        </div>
    </fieldset>
);

/**
 * Pop-in d'information sur les pixels et liens traçants présents dans les e-mails.
 */
export const PixelInfoModal = ({ onClose, mandant = 'aprilMoto' }) => {
    const { politiqueCookies } = MANDANTS[mandant];

    return (
        <Modal title={'Pixels &amp; liens traçants&nbsp;: à quoi ça sert&nbsp;?'} onClick={onClose}>
            <div className={'consentement-email__popin text-start'}>
                <h3 className={'consentement-email__sous-titre'}>Le pixel</h3>
                <p>
                    C'est une toute petite image, invisible à l'œil nu, glissée dans certains de nos e-mails. Elle ne
                    contient aucune donnée sensible&nbsp;: elle nous indique simplement <strong>si le message a été ouvert</strong>.
                </p>

                <h3 className={'consentement-email__sous-titre'}>Les liens traçants</h3>
                <p>
                    Certains liens de nos e-mails contiennent un petit code qui nous indique simplement{' '}
                    <strong>si vous avez cliqué dessus</strong>. Le lien vous emmène toujours exactement là où il
                    l'annonce&nbsp;: rien ne change pour vous au clic.
                </p>

                <hr />

                <h3 className={'consentement-email__sous-titre text-uppercase'}>Ce que ça nous permet de faire</h3>
                <ul className={'list-unstyled'}>
                    <li className={'d-flex align-items-start mb-1'}>
                        <span className={'check-icon w-20 me-2 flex-shrink-0'} aria-hidden="true" />
                        <span>Savoir si nos e-mails vous sont utiles, et quels sujets vous intéressent</span>
                    </li>
                    <li className={'d-flex align-items-start mb-1'}>
                        <span className={'check-icon w-20 me-2 flex-shrink-0'} aria-hidden="true" />
                        <span>Éviter de vous envoyer des messages qui ne vous intéressent pas</span>
                    </li>
                    <li className={'d-flex align-items-start mb-1'}>
                        <span className={'check-icon w-20 me-2 flex-shrink-0'} aria-hidden="true" />
                        <span>Améliorer, petit à petit, la pertinence de nos communications</span>
                    </li>
                </ul>

                <div className={'consentement-email__rassurance'}>
                    <h3 className={'consentement-email__sous-titre text-uppercase'}>Ce que ça ne fait jamais</h3>
                    <ul className={'mb-0'}>
                        <li>Ne collecte aucune donnée personnelle sensible</li>
                        <li>Ne vous suit jamais en dehors de vos e-mails</li>
                        <li>N'est jamais revendu ni partagé avec un tiers</li>
                    </ul>
                </div>

                <p className={'text-center f-13 mt-3'}>
                    Vous gardez la main&nbsp;: vous pouvez changer d'avis à tout moment depuis vos préférences, sans que
                    ça n'affecte votre devis.
                </p>
                <p className={'text-center f-13'}>
                    Pour en savoir plus, consultez notre{' '}
                    <LienExterne href={politiqueCookies}>Politique cookies et technologies similaires</LienExterne>.
                </p>

                <div className={'text-center'}>
                    <button type={'button'} className={'btn btn-primary medium-rounded'} onClick={onClose}>
                        J'ai compris
                    </button>
                </div>
            </div>
        </Modal>
    );
};

/**
 * Choix des consentements e-mail : relances / offres (EmailContactConsent) et mesure de l'utilisation
 * des e-mails par pixels et liens traçants (PixelConsent). Les deux réponses sont obligatoires.
 * @param mandant  'aprilMoto' (direct) ou 'amGestion' (courtier) : nom affiché et liens légaux
 */
export const ConsentementEmail = ({ className, mandant = 'aprilMoto' }) => {
    const [popinAffichee, setPopinAffichee] = useState(false);
    const { nom, lettreRgpd } = MANDANTS[mandant];

    return (
        <div className={`consentement-email ${className ?? ''}`}>
            <div className={'consentement-email__carte'}>
                <p className={'consentement-email__accroche mb-1'}>C'est vous qui décidez.</p>
                <p className={'consentement-email__texte mb-0'}>
                    Nous avons besoin de votre e-mail pour votre devis. Pour le reste, c'est vous qui choisissez.
                </p>

                <hr />

                <ChoixConsentement name={'DemandeDevis[EmailContactConsent]'} titre={`Recevoir des relances et offres ${nom}*`}>
                    Pour retrouver plus facilement votre devis et recevoir des offres susceptibles de vous intéresser.
                    Désinscription possible à tout moment.
                </ChoixConsentement>

                <hr />

                <ChoixConsentement name={'DemandeDevis[PixelConsent]'} titre={'Améliorer les e-mails que je reçois*'}>
                    J'accepte qu'{nom} mesure l'ouverture, les clics et l'utilisation de ses e-mails afin d'améliorer
                    ses communications.{' '}
                    <button type={'button'} className={'btn btn-link p-0 align-baseline fw-bold consentement-email__lien'} onClick={() => setPopinAffichee(true)}>
                        En savoir plus sur mes préférences&nbsp;›
                    </button>
                </ChoixConsentement>
            </div>

            <p className={'text-center text-grey f-12 mt-3 mb-1'}>*champs obligatoires</p>
            <p className={'text-center f-12'}>
                <LienExterne href={lettreRgpd} className={'text-grey'}>Comment utilisons-nous vos données&nbsp;?</LienExterne>
            </p>

            {popinAffichee && <PixelInfoModal mandant={mandant} onClose={() => setPopinAffichee(false)} />}
        </div>
    );
};

ConsentementEmail.propTypes = {
    className: PropTypes.string,
    mandant: PropTypes.oneOf(['aprilMoto', 'amGestion']),
};

PixelInfoModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    mandant: PropTypes.oneOf(['aprilMoto', 'amGestion']),
};
